import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { RootState } from "app/store";
import {
  activateUser,
  addUser,
  deactivateUser,
  editUser,
  fetchUserById,
  fetchUsers,
} from "app/thunks/userAPI";
import { User } from "interfaces/User";

export interface State {
  list: User[];
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
}

const initialState: State = {
  list: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  isFetching: false,
  error: undefined,
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(resetStore, () => initialState);
    builder.addCase(fetchUsers.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.isError = true;
      state.isFetching = false;
      state.error = action.payload;
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.list = action.payload as unknown as User[];
    });
    builder.addCase(fetchUserById.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchUserById.rejected, (state, action) => {
      state.isError = true;
      state.isFetching = false;
      state.error = action.payload;
    });
    builder.addCase(
      fetchUserById.fulfilled,
      (state, action: PayloadAction<User>) => {
        state.isSuccess = true;
        state.isFetching = false;
        state.list = [action.payload];
      },
    );
    builder.addCase(addUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addUser.fulfilled, (state) => {
      state.error = undefined;
      state.isLoading = false;
    });
    builder.addCase(addUser.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
    builder.addCase(activateUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(activateUser.fulfilled, (state, action) => {
      state.error = undefined;
      state.isLoading = false;
      const userId = action.meta.arg;
      const userIndex = state.list.findIndex((user) => user._id === userId);
      if (userIndex >= 0) {
        state.list[userIndex].isActive = true;
      }
    });
    builder.addCase(activateUser.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
    builder.addCase(deactivateUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deactivateUser.fulfilled, (state, action) => {
      state.error = undefined;
      state.isLoading = false;
      const userId = action.meta.arg;
      const userIndex = state.list.findIndex((user) => user._id === userId);
      if (userIndex >= 0) {
        state.list[userIndex].isActive = false;
      }
    });
    builder.addCase(deactivateUser.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
    builder.addCase(editUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(editUser.fulfilled, (state) => {
      state.error = undefined;
      state.isLoading = false;
    });
    builder.addCase(editUser.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
  },
});

// ------ Selectors ------
export const getUserActionState = (state: RootState) => ({
  isLoading: state.user.isLoading,
  error: state.user.error,
  users: state.user.list,
  isFetching: state.user.isFetching,
  isError: state.user.isError,
});

export default usersSlice.reducer;
